// Ngày công (attendance-service): hôm nay của tôi (đồng hồ vào/ra ca, pill thanh trên), chi tiết một ngày (điều chỉnh),
// chip mã ngày (.dc theo trạng thái đánh giá), địa điểm vào ca. Giờ vào/ra do máy chủ đóng dấu (serverTime ⇒ đồng hồ).
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { attendance } from '@cathouse/contracts';
import { http, qk } from '@/shared/api';
import { cx, fmtDate, fmtTime, syncServerTime } from '@/shared/lib';
import type { Option } from '@/shared/ui';

export type MyToday = attendance.MyToday;
export type WorkDayDetail = attendance.WorkDayDetail;
export type DayCode = attendance.DayCode;
export type DayStatus = attendance.DayStatus;
export type Place = attendance.Place;
export type AdjustCredit = attendance.AdjustCredit;
export type CheckInResult = attendance.CheckInResult;
export type CheckOutResult = attendance.CheckOutResult;

/** Địa điểm vào ca (mặc định Văn phòng) — nhãn như prototype PLACES. */
export const PLACE_OPTIONS: Option[] = attendance.PLACES.map((p) => [p, attendance.PLACE_LABEL[p]]);
export const DEFAULT_PLACE: Place = 'office';
/** Thứ trong tuần theo getDay() (0 = CN). */
export const WEEKDAY_SHORT = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'] as const;

/** Hôm nay của tôi (vào/ra ca, đơn nghỉ, hành động được phép) — làm mới mỗi phút, hiệu chỉnh đồng hồ theo serverTime. */
export const useMyToday = (enabled = true) =>
  useQuery({
    queryKey: qk.attendance.today(),
    queryFn: async ({ signal }) => {
      const r = await http.get<MyToday>('/attendance/me/today', { signal });
      syncServerTime(r.serverTime);
      return r;
    },
    refetchInterval: 60_000,
    enabled,
  });

/** Chi tiết một ngày công (bản ghi, điều chỉnh trước đó, version cho If-Match) — khi mở điều chỉnh. */
export const useWorkDay = (staffId: string | null | undefined, date: string | null | undefined) =>
  useQuery({
    queryKey: qk.attendance.day(staffId ?? '', date ?? ''),
    queryFn: ({ signal }) => http.get<WorkDayDetail>(`/attendance/staff/${staffId}/days/${date}`, { signal }),
    enabled: !!staffId && !!date,
    staleTime: 0,
  });

/** Số công dạng Việt Nam: 0,5 · 20,5. */
export const fmtCredit = (n: number | null | undefined) => String(n ?? 0).replace('.', ',');
/** "đủ 1 công" / "nửa công" / "0 công" (hộp "Ra ca sớm?"). */
export const creditLabel = (credit: number) => (credit === 1 ? 'đủ 1 công' : credit === 0.5 ? 'nửa công' : '0 công');
/** 'YYYY-MM-DD' ⇒ 'dd/mm/yyyy'. */
export const fmtDayKey = (dk: string) => dk.split('-').reverse().join('/');

/** Class chip mã ngày: .dc + trạng thái (ok, late, early, working, missingOut, absent, leave, adjusted, off, future). */
export const dayCodeClass = (status: DayStatus) => cx('dc', status);
/** .dc rộng cố định 24 px; mã 3 ký tự (½ĐC — điều chỉnh 0,5 công) tràn ô ⇒ nới theo nội dung, tối thiểu vẫn 24 px. */
export const DAY_CODE_FIT: CSSProperties = { width: 'auto', minWidth: 24, padding: '0 3px' };

/** Chú thích của một ô bảng công: "{ngày} · vào HH:mm ra HH:mm · {địa điểm} · muộn {n}′ · điều chỉnh: {giải trình}". */
export function dayTitle(dk: string, d: { checkInAt: string | null; checkOutAt: string | null; placeLabel: string | null; late: number; adjReason: string | null }) {
  let t = fmtDayKey(dk);
  if (d.checkInAt) t += ` · vào ${fmtTime(d.checkInAt)}${d.checkOutAt ? ` ra ${fmtTime(d.checkOutAt)}` : ''}${d.placeLabel ? ` · ${d.placeLabel}` : ''}`;
  if (d.late) t += ` · muộn ${d.late}′`;
  if (d.adjReason) t += ` · điều chỉnh: ${d.adjReason}`;
  return t;
}

/** Chip mã ngày (rỗng ⇒ không vẽ gì); `style` ghi đè thêm (ô lịch hẹp trên điện thoại). */
export function DayCodeChip({ code, status, title, style }: { code: DayCode | string; status: DayStatus; title?: string; style?: CSSProperties }) {
  if (!code) return null;
  return (
    <span className={dayCodeClass(status)} style={style ? { ...DAY_CODE_FIT, ...style } : DAY_CODE_FIT} title={title}>
      {code}
    </span>
  );
}

/** Nhãn đặt giữa câu: chỉ hạ chữ đầu ("Nghỉ ốm (BHXH)" ⇒ "nghỉ ốm (BHXH)") — giữ nguyên chữ viết tắt. */
const lcFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
/** "Hôm nay bạn có đơn nghỉ ốm (BHXH) (nửa ngày) đã duyệt." */
export const leaveTodayNote = (l: NonNullable<MyToday['leaveToday']>) => `Hôm nay bạn có đơn ${lcFirst(l.label)}${l.half ? ' (nửa ngày)' : ''} đã duyệt.`;

/** "CN, 25/10/2026". */
export const weekdayDate = (t: number, weekday: number) => `${WEEKDAY_SHORT[weekday]}, ${fmtDate(t)}`;
